import { useEffect, useState } from 'react';
import { getSimpleApiConfig } from './simple_api_config';

// Result of the SII configuration check. `null` means "not yet known".
type SiiConfiguredState = boolean | null;

const cacheKey = (localId: number) => `siiConfigured:${localId}`;

/**
 * Reports whether the local has a valid SII (SimpleAPI) configuration.
 *
 * Detection mirrors Sii.tsx::loadConfiguration:
 *  - non-empty `config` object  -> configured (true)
 *  - HTTP 404                   -> not configured (false)
 *  - any other error            -> UNKNOWN: we keep the previous value and never cache it,
 *                                  so a transient network failure can't permanently hide the feature.
 *
 * Only the resolved boolean is cached in sessionStorage (never the config object, which holds
 * sensitive fields), keyed by localId to stay correct when switching locals.
 */
export const useSiiConfigured = (localId: number): SiiConfiguredState => {
  const [configured, setConfigured] = useState<SiiConfiguredState>(() => {
    if (!localId) return false;
    const cached = sessionStorage.getItem(cacheKey(localId));
    return cached === null ? null : cached === 'true';
  });

  useEffect(() => {
    if (!localId) {
      setConfigured(false);
      return;
    }

    let cancelled = false;

    const cached = sessionStorage.getItem(cacheKey(localId));
    if (cached !== null) {
      setConfigured(cached === 'true');
      return;
    }

    (async () => {
      try {
        const response = await getSimpleApiConfig(localId);
        const configData = response.data?.config;
        const isValid =
          !!configData &&
          typeof configData === 'object' &&
          Object.keys(configData).length > 0;

        if (cancelled) return;
        sessionStorage.setItem(cacheKey(localId), String(isValid));
        setConfigured(isValid);
      } catch (error: any) {
        if (cancelled) return;
        if (error?.response?.status === 404) {
          sessionStorage.setItem(cacheKey(localId), 'false');
          setConfigured(false);
        } else {
          // Unknown (e.g. network error): don't cache, don't gate the feature off permanently.
          setConfigured((prev) => prev ?? false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [localId]);

  return configured;
};

/** Clears the cached SII-configured flag (call after saving/deleting SII config). */
export const clearSiiConfiguredCache = (localId: number): void => {
  sessionStorage.removeItem(cacheKey(localId));
};

export default useSiiConfigured;
